import { FilterConfigFormData, StrategyFormData } from "@/pages/AutoTrade";
import { Bucket } from "@/pages/bucket-management";
import { FilterApiSchema } from "@/pages/manual-testing";
import { OrderSettingsFormData } from "@/pages/order-settings";
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormReturn,
} from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { timeOptions } from "../utilities/util";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Wand2, ListPlus, Trash2, ChevronDown, Stamp } from "lucide-react";
import { useState, useCallback } from "react";

type FilterConfigurationFormProps = {
  form: UseFormReturn<StrategyFormData>;
  fields: FieldArrayWithId<StrategyFormData, "filterConfigs">[];
  append: UseFieldArrayAppend<StrategyFormData, "filterConfigs">;
  remove: UseFieldArrayRemove;
  buckets?: Bucket[];
  orderSettings?: OrderSettingsFormData[];
  filters?: FilterApiSchema[];
};

type DefaultsState = {
  bucketIds: string[];
  orderSettingId: string;
  timeInMinutes: number;
  exitFilterIds: string[];
};

const emptyDefaults: DefaultsState = {
  bucketIds: [],
  orderSettingId: "",
  timeInMinutes: 60,
  exitFilterIds: [],
};

// ─── Shared: Bucket multi-select ──────────────────────────────────────────────
function BucketMultiSelect({
  value,
  onChange,
  buckets,
  placeholder = "Select buckets",
}: {
  value: string[];
  onChange: (v: string[]) => void;
  buckets?: Bucket[];
  placeholder?: string;
}) {
  const allIds = buckets?.map((b) => b._id) ?? [];
  const isAll = allIds.length > 0 && allIds.every((id) => value.includes(id));

  const label =
    value.length === 0
      ? placeholder
      : value.length === buckets?.length
      ? "All Buckets"
      : buckets
          ?.filter((b) => value.includes(b._id))
          .map((b) => b.name)
          .join(", ") ?? placeholder;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between h-9 bg-white border border-gray-200 hover:border-violet-300 rounded-lg text-xs font-normal"
        >
          <span className="truncate text-left text-gray-700">{label}</span>
          <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-40" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3 max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 z-50">
        <div className="flex justify-end mb-2">
          <button
            type="button"
            className="text-xs text-violet-600 hover:underline font-medium"
            onClick={() => onChange(isAll ? [] : allIds)}
          >
            {isAll ? "Clear all" : "Select all"}
          </button>
        </div>
        <div className="space-y-1">
          {buckets?.map((bucket) => {
            const checked = value.includes(bucket._id);
            return (
              <div
                key={bucket._id}
                className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-violet-50 cursor-pointer transition-colors"
                onClick={() =>
                  onChange(
                    checked
                      ? value.filter((id) => id !== bucket._id)
                      : [...value, bucket._id]
                  )
                }
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => {}}
                  className="data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500 pointer-events-none"
                />
                <span className="text-xs text-gray-700">{bucket.name}</span>
              </div>
            );
          })}
          {(!buckets || buckets.length === 0) && (
            <p className="text-xs text-center text-gray-400 py-3">
              No buckets available
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Shared: Exit filter multi-select ─────────────────────────────────────────
function ExitFilterMultiSelect({
  value,
  onChange,
  filters,
  placeholder = "Select exit filters",
}: {
  value: string[];
  onChange: (v: string[]) => void;
  filters?: FilterApiSchema[];
  placeholder?: string;
}) {
  const exitFilters = filters?.filter((f) => f.type === "EXIT") ?? [];

  const label =
    value.length === 0
      ? placeholder
      : exitFilters
          .filter((f) => value.includes(f.id ?? ""))
          .map((f) => f.function_name)
          .join(", ") || placeholder;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between h-9 bg-white border border-gray-200 hover:border-violet-300 rounded-lg text-xs font-normal"
        >
          <span className="truncate text-left text-gray-700">{label}</span>
          <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-40" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3 max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 z-50">
        <div className="space-y-1">
          {exitFilters.map((filter) => {
            const checked = value.includes(filter.id ?? "");
            return (
              <div
                key={filter.id}
                className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-violet-50 cursor-pointer transition-colors"
                onClick={() =>
                  onChange(
                    checked
                      ? value.filter((id) => id !== filter.id)
                      : [...value, filter.id ?? ""]
                  )
                }
              >
                <Checkbox
                  checked={checked}
                  className="data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500 pointer-events-none"
                />
                <span className="text-xs text-gray-700">
                  {filter.function_name}
                </span>
              </div>
            );
          })}
          {exitFilters.length === 0 && (
            <p className="text-xs text-center text-gray-400 py-3">
              No exit filters available
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Bulk-add modal ────────────────────────────────────────────────────────────
function BulkAddModal({
  filters,
  existingFilterIds,
  defaults,
  onAdd,
  onClose,
}: {
  filters?: FilterApiSchema[];
  existingFilterIds: string[];
  defaults: DefaultsState;
  onAdd: (filterIds: string[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);

  // Only non-EXIT filters can be primary filter configs
  const available = filters?.filter((f) => f.type !== "EXIT") ?? [];

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleType = (type: string) => {
    const typeIds = available
      .filter((f) => f.type === type)
      .map((f) => f.id ?? "")
      .filter((id) => !existingFilterIds.includes(id));
    const allSelected = typeIds.every((id) => selected.includes(id));
    setSelected((prev) =>
      allSelected
        ? prev.filter((id) => !typeIds.includes(id))
        : [...new Set([...prev, ...typeIds])]
    );
  };

  const types = [...new Set(available.map((f) => f.type))];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-violet-50 to-indigo-50 shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">
              Add Multiple Filters
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Selected filters will inherit your current defaults
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none w-6 h-6 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        {/* Defaults preview */}
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 shrink-0">
          <p className="text-xs font-medium text-amber-800 mb-1.5">
            Defaults applied to all selected:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {defaults.bucketIds.length > 0 ? (
              <Badge variant="secondary" className="text-xs bg-white border border-amber-200 font-normal">
                {defaults.bucketIds.length} bucket{defaults.bucketIds.length !== 1 ? "s" : ""}
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-xs bg-white border border-red-200 text-red-500 font-normal">
                No buckets set
              </Badge>
            )}
            {defaults.orderSettingId ? (
              <Badge variant="secondary" className="text-xs bg-white border border-amber-200 font-normal">
                Order setting ✓
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-xs bg-white border border-red-200 text-red-500 font-normal">
                No order setting
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs bg-white border border-amber-200 font-normal">
              {timeOptions.find((o) => o.value === defaults.timeInMinutes)?.label ??
                `${defaults.timeInMinutes}m`}
            </Badge>
            {defaults.exitFilterIds.length > 0 && (
              <Badge variant="secondary" className="text-xs bg-white border border-amber-200 font-normal">
                {defaults.exitFilterIds.length} exit filter{defaults.exitFilterIds.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
        </div>

        {/* Filter list */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {types.map((type) => {
            const typeFilters = available
              .filter((f) => f.type === type)
              .sort((a, b) => a.function_name.localeCompare(b.function_name));

            if (typeFilters.length === 0) return null;

            const selectableIds = typeFilters
              .filter((f) => !existingFilterIds.includes(f.id ?? ""))
              .map((f) => f.id ?? "");
            const allTypeSelected =
              selectableIds.length > 0 &&
              selectableIds.every((id) => selected.includes(id));

            return (
              <div key={type}>
                {/* Group header with select-all */}
                <div
                  className="flex items-center gap-2 mb-2 cursor-pointer group"
                  onClick={() => toggleType(type)}
                >
                  <Checkbox
                    checked={allTypeSelected}
                    onCheckedChange={() => {}}
                    className="data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500 pointer-events-none"
                  />
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-400 group-hover:text-violet-600 transition-colors">
                    {type} Filters
                  </span>
                  <span className="text-xs text-gray-300">
                    ({typeFilters.length})
                  </span>
                </div>

                <div className="space-y-1 pl-6">
                  {typeFilters.map((filter) => {
                    const isExisting = existingFilterIds.includes(filter.id ?? "");
                    const isSelected = selected.includes(filter.id ?? "");

                    return (
                      <div
                        key={filter.id}
                        className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isExisting
                            ? "opacity-40 cursor-not-allowed bg-gray-50"
                            : "hover:bg-violet-50 cursor-pointer"
                        }`}
                        onClick={() => !isExisting && toggle(filter.id ?? "")}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Checkbox
                            checked={isSelected}
                            disabled={isExisting}
                            onCheckedChange={() => {}}
                            className="data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500 pointer-events-none shrink-0"
                          />
                          <span className="text-sm text-gray-700 truncate">
                            {filter.function_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs text-gray-400">
                            {filter.category}
                          </span>
                          {isExisting && (
                            <Badge variant="secondary" className="text-xs py-0 px-1.5">
                              Added
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {available.length === 0 && (
            <p className="text-sm text-center text-gray-400 py-8">
              No filters available
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50 shrink-0">
          <span className="text-sm text-gray-500">
            {selected.length} filter{selected.length !== 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={selected.length === 0}
              onClick={() => {
                onAdd(selected);
                onClose();
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              <ListPlus className="w-4 h-4 mr-1.5" />
              Add {selected.length > 0 ? selected.length : ""} Filter
              {selected.length !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
function FilterConfigurationForm({
  form,
  fields,
  append,
  remove,
  buckets,
  orderSettings,
  filters,
}: FilterConfigurationFormProps) {
  const [defaults, setDefaults] = useState<DefaultsState>(emptyDefaults);
  const [showBulkAdd, setShowBulkAdd] = useState(false);

  const currentFilterIds = fields.map(
    (_, i) => form.getValues(`filterConfigs.${i}.filterId`) as string
  );

  // Stamp defaults onto every existing row
  const applyDefaultsToAll = useCallback(() => {
    fields.forEach((_, i) => {
      if (defaults.bucketIds.length > 0)
        form.setValue(`filterConfigs.${i}.bucketIds`, defaults.bucketIds);
      if (defaults.orderSettingId)
        form.setValue(`filterConfigs.${i}.orderSettingId`, defaults.orderSettingId);
      if (defaults.timeInMinutes)
        form.setValue(`filterConfigs.${i}.timeInMinutes`, defaults.timeInMinutes);
      // exitFilterIds always applied (can be intentionally empty)
      form.setValue(`filterConfigs.${i}.exitFilterIds`, defaults.exitFilterIds);
    });
  }, [defaults, fields, form]);

  // Add a single blank row with defaults pre-filled
  const addSingleRow = () => {
    append({
      filterId: "",
      bucketIds: defaults.bucketIds,
      timeInMinutes: defaults.timeInMinutes,
      orderSettingId: defaults.orderSettingId,
      exitFilterIds: defaults.exitFilterIds,
    });
  };

  // Add many rows from bulk-add modal, all pre-filled with defaults
  const handleBulkAdd = useCallback(
    (filterIds: string[]) => {
      filterIds.forEach((filterId) => {
        append({
          filterId,
          bucketIds: defaults.bucketIds,
          timeInMinutes: defaults.timeInMinutes,
          orderSettingId: defaults.orderSettingId,
          exitFilterIds: defaults.exitFilterIds,
        });
      });
    },
    [append, defaults]
  );

  return (
    <div className="space-y-4">

      {/* ── Defaults bar ─────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-dashed border-violet-300 bg-violet-50/50 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Wand2 className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-semibold text-violet-900">Row Defaults</span>
          <span className="text-xs text-violet-400 hidden sm:inline">
            — set once; every new row inherits these values automatically
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Buckets</label>
            <BucketMultiSelect
              value={defaults.bucketIds}
              onChange={(v) => setDefaults((d) => ({ ...d, bucketIds: v }))}
              buckets={buckets}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Order Setting</label>
            <Select
              value={defaults.orderSettingId}
              onValueChange={(v) => setDefaults((d) => ({ ...d, orderSettingId: v }))}
            >
              <SelectTrigger className="h-9 text-xs bg-white border-gray-200 hover:border-violet-300">
                <SelectValue placeholder="Select setting" />
              </SelectTrigger>
              <SelectContent>
                {orderSettings?.map((o) => (
                  <SelectItem key={o.id} value={o.id ?? ""}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Cutoff Time</label>
            <Select
              value={defaults.timeInMinutes.toString()}
              onValueChange={(v) => setDefaults((d) => ({ ...d, timeInMinutes: parseInt(v) }))}
            >
              <SelectTrigger className="h-9 text-xs bg-white border-gray-200 hover:border-violet-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Exit Filters</label>
            <ExitFilterMultiSelect
              value={defaults.exitFilterIds}
              onChange={(v) => setDefaults((d) => ({ ...d, exitFilterIds: v }))}
              filters={filters}
            />
          </div>
        </div>

        {/* Apply-to-all — only shown when rows already exist */}
        {fields.length > 0 && (
          <div className="mt-3 pt-3 border-t border-violet-200 flex items-center justify-between gap-3">
            <p className="text-xs text-violet-500">
              {fields.length} existing row{fields.length !== 1 ? "s" : ""} — stamp these defaults onto all of them?
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-100 shrink-0"
              onClick={applyDefaultsToAll}
            >
              <Stamp className="w-3 h-3 mr-1" />
              Apply to All Rows
            </Button>
          </div>
        )}
      </div>

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Filter Configs</span>
          {fields.length > 0 && (
            <Badge variant="secondary" className="text-xs tabular-nums">{fields.length}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" className="text-xs h-8" onClick={addSingleRow}>
            + Add Row
          </Button>
          <Button
            type="button"
            size="sm"
            className="text-xs h-8 bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => setShowBulkAdd(true)}
          >
            <ListPlus className="w-3.5 h-3.5 mr-1" />
            Add Multiple
          </Button>
        </div>
      </div>

      {/* ── Column headers (desktop only) ────────────────────────────────── */}
      {fields.length > 0 && (
        <div className="hidden md:grid md:grid-cols-[1.4fr_1.8fr_1.3fr_0.9fr_1.8fr_2rem] gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wide">
          <span>Filter</span>
          <span>Buckets</span>
          <span>Order Setting</span>
          <span>Cutoff</span>
          <span>Exit Filters</span>
          <span></span>
        </div>
      )}

      {/* ── Rows ─────────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        {fields.map((field, index) => {
          const fieldName = (name: keyof FilterConfigFormData) =>
            `filterConfigs.${index}.${name}` as const;

          return (
            <div
              key={field.id}
              className="grid grid-cols-1 md:grid-cols-[1.4fr_1.8fr_1.3fr_0.9fr_1.8fr_2rem] items-start gap-2 p-3 border border-gray-200 rounded-xl bg-white hover:border-gray-300 transition-colors group"
            >
              {/* Filter ID */}
              <FormField
                control={form.control}
                name={fieldName("filterId")}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-gray-500 md:sr-only">Filter</FormLabel>
                    <FormControl>
                      <Select value={String(field.value)} onValueChange={field.onChange}>
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Select a filter" />
                        </SelectTrigger>
                        <SelectContent>
                          {["BUY", "SELL", "EXIT"].map((type) => {
                            const typeFilters = filters?.filter((f) => f.type === type) || [];
                            if (typeFilters.length === 0) return null;
                            return (
                              <div key={type}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                                  {type} Filters
                                </div>
                                {typeFilters
                                  .sort((a, b) => a.function_name.localeCompare(b.function_name))
                                  .map((filter) => (
                                    <SelectItem
                                      key={filter.id ?? ""}
                                      value={filter.id ?? ""}
                                      className="pl-5 text-xs"
                                    >
                                      <span>{filter.function_name}</span>
                                      <span className="ml-1.5 text-gray-400">({filter.category})</span>
                                    </SelectItem>
                                  ))}
                              </div>
                            );
                          })}
                          {(!filters || filters.length === 0) && (
                            <div className="px-2 py-6 text-center text-sm text-gray-400">
                              No filters available
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Buckets */}
              <FormField
                control={form.control}
                name={fieldName("bucketIds")}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-gray-500 md:sr-only">Buckets</FormLabel>
                    <BucketMultiSelect
                      value={(field.value as string[]) || []}
                      onChange={field.onChange}
                      buckets={buckets}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Order Setting */}
              <FormField
                control={form.control}
                name={fieldName("orderSettingId")}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-gray-500 md:sr-only">Order Setting</FormLabel>
                    <FormControl>
                      <Select value={String(field.value)} onValueChange={field.onChange}>
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {orderSettings?.map((o) => (
                            <SelectItem key={o.id} value={o.id ?? ""} className="text-xs">
                              {o.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Cutoff Time */}
              <FormField
                control={form.control}
                name={fieldName("timeInMinutes")}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-gray-500 md:sr-only">Cutoff</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Time" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {timeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value.toString()} className="text-xs">
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Exit Filters */}
              <FormField
                control={form.control}
                name={fieldName("exitFilterIds")}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-gray-500 md:sr-only">Exit Filters</FormLabel>
                    <ExitFilterMultiSelect
                      value={(field.value as string[]) || []}
                      onChange={field.onChange}
                      filters={filters}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Remove — fades in on row hover */}
              <div className="flex items-center justify-end md:justify-center md:pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  onClick={() => remove(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-sm text-gray-400 mb-3">No filter configs yet</p>
          <div className="flex items-center justify-center gap-2">
            <Button type="button" size="sm" variant="outline" className="text-xs" onClick={addSingleRow}>
              + Add Row
            </Button>
            <Button
              type="button"
              size="sm"
              className="text-xs bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => setShowBulkAdd(true)}
            >
              <ListPlus className="w-3.5 h-3.5 mr-1" />
              Add Multiple
            </Button>
          </div>
        </div>
      )}

      {/* Bulk-add modal */}
      {showBulkAdd && (
        <BulkAddModal
          filters={filters}
          existingFilterIds={currentFilterIds}
          defaults={defaults}
          onAdd={handleBulkAdd}
          onClose={() => setShowBulkAdd(false)}
        />
      )}
    </div>
  );
}

export default FilterConfigurationForm;